/* ==========================================================================
   Address Autocomplete Component
   Street input with debounced typeahead dropdown
   Task F38: Address autocomplete component + integration
   ========================================================================== */

import { useState, useRef, useEffect, useCallback, useId } from 'react'
import type { AxiosInstance } from 'axios'
import { Loader2 } from 'lucide-react'
import { autocompleteAddress, type AddressSuggestion } from '@/api/address'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

export interface AddressAutocompleteProps {
  /** Current street value */
  value: string
  /** Called when the street text changes (typing) */
  onChange: (street: string) => void
  /** Called when user selects a suggestion from the dropdown */
  onAddressSelect: (suggestion: AddressSuggestion) => void
  placeholder?: string
  disabled?: boolean
  error?: boolean
  id?: string
  onBlur?: () => void
  /** Axios instance — pass adminClient for admin context, defaults to provider apiClient */
  apiClient?: AxiosInstance
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function AddressAutocomplete({
  value,
  onChange,
  onAddressSelect,
  placeholder = '123 Main St',
  disabled = false,
  error = false,
  id,
  onBlur,
  apiClient,
}: AddressAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  // Suppress dropdown immediately after selecting a suggestion
  const justSelectedRef = useRef(false)

  const generatedId = useId()
  const inputId = id || generatedId
  const listboxId = `${inputId}-listbox`

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      if (abortRef.current) abortRef.current.abort()
    }
  }, [])

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Fetch suggestions with debounce
  const fetchSuggestions = useCallback(
    (query: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      if (abortRef.current) abortRef.current.abort()

      if (query.length < 2) {
        setSuggestions([])
        setIsOpen(false)
        setIsLoading(false)
        return
      }

      setIsLoading(true)

      debounceRef.current = setTimeout(async () => {
        const controller = new AbortController()
        abortRef.current = controller

        try {
          const results = apiClient
            ? await autocompleteAddress(query, apiClient)
            : await autocompleteAddress(query)
          if (!controller.signal.aborted) {
            setSuggestions(results)
            setIsOpen(results.length > 0)
            setActiveIndex(-1)
          }
        } catch {
          // Graceful degradation — silently fail, input remains a plain text field
          if (!controller.signal.aborted) {
            setSuggestions([])
            setIsOpen(false)
          }
        } finally {
          if (!controller.signal.aborted) {
            setIsLoading(false)
          }
        }
      }, 300)
    },
    [apiClient]
  )

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newValue = e.target.value
    onChange(newValue)

    if (justSelectedRef.current) {
      justSelectedRef.current = false
      return
    }

    fetchSuggestions(newValue)
  }

  function handleSelect(suggestion: AddressSuggestion) {
    justSelectedRef.current = true
    setSuggestions([])
    setIsOpen(false)
    setActiveIndex(-1)
    onChange(suggestion.street)
    onAddressSelect(suggestion)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!isOpen || suggestions.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActiveIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setActiveIndex((prev) =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        )
        break
      case 'Enter':
        e.preventDefault()
        if (activeIndex >= 0 && activeIndex < suggestions.length) {
          handleSelect(suggestions[activeIndex])
        }
        break
      case 'Escape':
        setIsOpen(false)
        setActiveIndex(-1)
        break
    }
  }

  function handleInputBlur() {
    // Delay to allow click on suggestion to fire first
    setTimeout(() => {
      setIsOpen(false)
      onBlur?.()
    }, 150)
  }

  // Scroll active option into view
  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const option = listRef.current.children[activeIndex] as HTMLElement
      option?.scrollIntoView({ block: 'nearest' })
    }
  }, [activeIndex])

  // Input styling — matches existing Input component from ui/Input.tsx
  const inputBaseStyles = [
    'w-full',
    'px-3 py-3',
    'text-base',
    'font-sans',
    'text-[var(--color-charcoal)]',
    'placeholder:text-[var(--color-gray-500)]',
    'bg-white',
    'border',
    'rounded-md',
    'transition-[border-color,box-shadow] duration-150 ease-in-out',
    'focus:outline-none',
  ]

  const inputStateStyles = error
    ? [
        'border-[var(--color-error)]',
        'focus:border-[var(--color-error)]',
        'focus:shadow-[0_0_0_3px_rgba(198,40,40,0.2)]',
      ]
    : [
        'border-[var(--color-gray-300)]',
        'focus:border-[var(--pharmacy-primary)]',
        'focus:shadow-[0_0_0_3px_var(--pharmacy-focus-ring)]',
      ]

  const inputDisabledStyles = disabled
    ? ['bg-[var(--color-gray-100)]', 'text-[var(--color-gray-500)]', 'cursor-not-allowed']
    : []

  const inputClasses = [...inputBaseStyles, ...inputStateStyles, ...inputDisabledStyles]
    .join(' ')

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={isOpen ? listboxId : undefined}
          aria-activedescendant={
            isOpen && activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined
          }
          aria-autocomplete="list"
          autoComplete="off"
          className={inputClasses}
          placeholder={placeholder}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleInputBlur}
          disabled={disabled}
        />
        {isLoading && (
          <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <Loader2
              className="h-4 w-4 text-[var(--color-gray-500)] animate-spin"
              aria-hidden="true"
            />
          </span>
        )}
      </div>

      {isOpen && suggestions.length > 0 && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none"
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={`${suggestion.street}-${suggestion.zipcode}-${index}`}
              id={`${listboxId}-option-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              className={`cursor-pointer select-none px-4 py-2.5 transition-colors ${
                index === activeIndex
                  ? 'bg-[var(--color-soft-lavender)]'
                  : 'hover:bg-gray-50'
              }`}
              onMouseDown={() => handleSelect(suggestion)}
              onMouseEnter={() => setActiveIndex(index)}
            >
              <span className="block text-sm font-medium text-[var(--color-charcoal)]">
                {suggestion.street}
              </span>
              <span className="block text-xs text-[var(--color-gray-500)]">
                {suggestion.city}, {suggestion.state} {suggestion.zipcode}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default AddressAutocomplete
